import { createAsyncThunk } from "@reduxjs/toolkit";
import * as leadsQueueApi from "../services/leadsQueueApi";
import { getAllCompanies } from "../services/companyService";
import { getErrorMessage } from "../utils/errorUtils";

export const fetchCompanies = createAsyncThunk(
  "leadsQueue/fetchCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const signature = localStorage.getItem("signature");
      return await getAllCompanies(signature);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchLeadsForms = createAsyncThunk(
  "leadsQueue/fetchLeadsForms",
  async (
    {
      companyId,
      companyType,
    }: {
      companyId: string;
      companyType: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.getLeadsForms(
        signature,
        companyId,
        companyType,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchLeadsQueueData = createAsyncThunk(
  "leadsQueue/fetchLeadsQueueData",
  async (
    {
      companyId,
      companyType,
      sourceType,
      sourceFilter,
      dateRange,
      formId,
      page,
      limit,
      search,
    }: {
      companyId: string;
      companyType: string;
      sourceType: string;
      sourceFilter: any;
      dateRange: any;
      formId: number | null;
      page: number;
      limit: number;
      search?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.getLeadsQueue(
        signature,
        companyId,
        companyType,
        sourceType,
        sourceFilter,
        dateRange,
        page,
        limit,
        formId,
        search || "",
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchLeadsHistoryData = createAsyncThunk(
  "leadsQueue/fetchLeadsHistoryData",
  async (
    {
      companyId,
      companyType,
      sourceType,
      sourceFilter,
      dateRange,
      page,
      limit,
      formId,
      search,
      assignedTo,
    }: {
      companyId: string;
      companyType: string;
      sourceType: string;
      sourceFilter: any;
      dateRange: any;
      page: number;
      limit: number;
      formId: number | null;
      search?: string;
      assignedTo?: string | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.getLeadsHistory(
        signature,
        companyId,
        companyType,
        sourceType,
        sourceFilter,
        dateRange,
        page,
        limit,
        formId,
        search || "",
        assignedTo,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const assignLeads = createAsyncThunk(
  "leadsQueue/assignLeads",
  async (
    {
      leadIds,
      userId,
      companyId,
      companyType,
    }: {
      leadIds: React.Key[];
      userId: string;
      companyId: string;
      companyType: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.bulkAssignLeads(
        signature,
        leadIds,
        userId,
        companyId,
        companyType,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchLeadBankData = createAsyncThunk(
  "leadsQueue/fetchLeadBankData",
  async (
    {
      companyId,
      companyType,
      facebookFormId,
      leadSourceChild,
      page,
      limit,
      search,
    }: {
      companyId: string;
      companyType: string;
      facebookFormId: string | null;
      leadSourceChild?: string | null;
      page: number;
      limit: number;
      search?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.getLeadBankLeads(
        signature,
        companyId,
        companyType,
        facebookFormId,
        page,
        limit,
        leadSourceChild ?? null,
        search || "",
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const fetchLeadBankForms = createAsyncThunk(
  "leadsQueue/fetchLeadBankForms",
  async (
    {
      companyId,
      companyType,
    }: {
      companyId: string;
      companyType: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.getLeadBankForms(
        signature,
        companyId,
        companyType,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const assignLeadBank = createAsyncThunk(
  "leadsQueue/assignLeadBank",
  async (
    {
      leadIds,
      userId,
      crmFormId,
      companyId,
      companyType,
      assignedLeadType,
    }: {
      leadIds: React.Key[];
      userId: string | null;
      crmFormId: number;
      companyId: string;
      companyType: string;
      assignedLeadType?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await leadsQueueApi.assignLeadBankLeads(
        signature,
        leadIds,
        userId,
        crmFormId,
        companyId,
        companyType,
        assignedLeadType,
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
